/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Custom Pages Management View (Task 3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileCode,
  Plus,
  RefreshCw,
  Search,
  Edit2,
  Trash2,
  Globe,
  Calendar,
  X,
  Check,
  AlertTriangle,
  FileText,
  Link,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import type {
  CmsPage,
  CreateCmsPageInput,
  UpdateCmsPageInput,
} from '@sofia/shared-types';

export const CustomPagesManagementView: React.FC = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [localeFilter, setLocaleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<CmsPage | null>(null);
  const [deletingPage, setDeletingPage] = useState<CmsPage | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const { data: pagesData, isLoading, refetch } = useQuery<{
    items: CmsPage[];
    total: number;
  }>({
    queryKey: ['admin-cms-pages', localeFilter, statusFilter, searchQuery],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 100 };
      if (localeFilter !== 'all') params.locale = localeFilter;
      if (statusFilter !== 'all') params.isPublished = statusFilter === 'published';
      if (searchQuery.trim().length > 0) params.search = searchQuery.trim();

      const res = await adminClient.get('/api/v1/admin/cms/pages', params);
      return res.data || { items: [], total: 0 };
    },
  });

  const pagesList = pagesData?.items || [];

  const createPageMutation = useMutation({
    mutationFn: (payload: CreateCmsPageInput) => adminClient.post('/api/v1/admin/cms/pages', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-pages'] });
      setIsModalOpen(false);
      showToast('success', t('admin.cms.pageCreated') || 'Page created successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to create page.');
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCmsPageInput }) =>
      adminClient.patch(`/api/v1/admin/cms/pages/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-pages'] });
      setIsModalOpen(false);
      setEditingPage(null);
      showToast('success', t('admin.cms.pageUpdated') || 'Page updated successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to update page.');
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: (id: string) => adminClient.delete(`/api/v1/admin/cms/pages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-pages'] });
      setDeletingPage(null);
      showToast('success', t('admin.cms.pageDeleted') || 'Page deleted successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to delete page.');
    },
  });

  const columns = useMemo<ColumnDef<CmsPage>[]>(() => [
    {
      id: 'title',
      header: t('admin.cms.colTitle') || 'Page Title',
      accessorKey: 'title',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
            <FileText className="w-4 h-4" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-xs text-foreground truncate">{row.title}</span>
            <span className="text-[10px] text-muted-fg font-mono flex items-center gap-1">
              <Link className="w-2.5 h-2.5 opacity-60" />
              /{row.slug}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'locale',
      header: t('admin.cms.colLocale') || 'Locale',
      accessorKey: 'locale',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase font-semibold text-muted-fg bg-surface-elevated px-2 py-0.5 rounded border border-surface-border">
          <Globe className="w-3 h-3 text-muted-fg/70" />
          {row.locale}
        </span>
      ),
    },
    {
      id: 'metaTitle',
      header: t('admin.cms.colSeoTitle') || 'SEO Title',
      accessorKey: 'metaTitle',
      cell: ({ row }) => (
        <span className="text-xs text-muted-fg line-clamp-1 max-w-[180px]">
          {row.metaTitle || '—'}
        </span>
      ),
    },
    {
      id: 'sortOrder',
      header: t('admin.cms.colOrder') || 'Order',
      accessorKey: 'sortOrder',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-fg">#{row.sortOrder}</span>
      ),
    },
    {
      id: 'status',
      header: t('admin.cms.colStatus') || 'Status',
      accessorKey: 'isPublished',
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
            row.isPublished
              ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
              : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${row.isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {row.isPublished ? (t('admin.cms.statusPublished') || 'Published') : (t('admin.cms.statusDraft') || 'Draft')}
        </span>
      ),
    },
    {
      id: 'date',
      header: t('admin.cms.colDate') || 'Updated',
      accessorKey: 'updatedAt',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-[11px] text-muted-fg font-mono">
          <Calendar className="w-3 h-3 opacity-60" />
          {new Date(row.updatedAt * 1000).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => {
              setEditingPage(row);
              setIsModalOpen(true);
            }}
            className="p-1.5 text-muted-fg hover:text-foreground hover:bg-surface-elevated rounded-lg transition-colors"
            title="Edit Page"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingPage(row)}
            className="p-1.5 text-muted-fg hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            title="Delete Page"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [t]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-xl text-xs font-semibold backdrop-blur-md animate-fade-in ${
            toastMessage.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
              : 'bg-destructive/10 border-destructive/30 text-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {t('admin.cms.pagesTitle') || 'Custom Pages'}
            </h2>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-primary/10 text-primary border border-primary/20">
              {pagesList.length} Pages
            </span>
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.cms.pagesSubtitle') || 'Create and manage legal policies (Terms, Privacy), FAQ, and informational pages.'}
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingPage(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-white font-medium text-xs hover:bg-primary/90 shadow-sm transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>{t('admin.cms.btnNewPage') || 'New Page'}</span>
        </button>
      </div>

      {/* Filter Toolbar */}
      <div className="p-3 rounded-xl bg-surface border border-surface-border flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-muted-fg absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.cms.searchPagesPlaceholder') || 'Search pages by title or slug...'}
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary/50"
          />
        </div>

        <select
          value={localeFilter}
          onChange={(e) => setLocaleFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.cms.filterAllLocales') || 'All Locales'}</option>
          <option value="en">English (en)</option>
          <option value="vi">Tiếng Việt (vi)</option>
          <option value="ja">日本語 (ja)</option>
          <option value="ko">한국어 (ko)</option>
          <option value="es">Español (es)</option>
          <option value="zh">中文 (zh)</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.cms.filterAllStatuses') || 'All Statuses'}</option>
          <option value="published">{t('admin.cms.statusPublished') || 'Published'}</option>
          <option value="draft">{t('admin.cms.statusDraft') || 'Draft'}</option>
        </select>

        <button
          type="button"
          onClick={() => refetch()}
          className="p-1.5 rounded-lg border border-surface-border hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-colors"
          title="Refresh Pages"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Pages DataTable */}
      <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
        <DataTable columns={columns} data={pagesList} isLoading={isLoading} />
      </div>

      {/* Page Create / Edit Modal */}
      {isModalOpen && (
        <CustomPageModal
          page={editingPage}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingPage(null);
          }}
          onSave={(payload) => {
            if (editingPage) {
              updatePageMutation.mutate({ id: editingPage.id, payload });
            } else {
              createPageMutation.mutate(payload as CreateCmsPageInput);
            }
          }}
          isSubmitting={createPageMutation.isPending || updatePageMutation.isPending}
        />
      )}

      {/* Delete Page Confirm Modal */}
      {deletingPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm rounded-2xl bg-surface border border-surface-border p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.cms.confirmDeletePage') || 'Delete Custom Page?'}
              </h3>
            </div>
            <p className="text-xs text-muted-fg leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-foreground">"{deletingPage.title}"</span>? Links leading to this URL will return 404.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingPage(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
              >
                {t('admin.common.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                disabled={deletePageMutation.isPending}
                onClick={() => deletePageMutation.mutate(deletingPage.id)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-destructive text-white hover:bg-destructive/90 shadow-sm disabled:opacity-50"
              >
                {deletePageMutation.isPending ? 'Deleting...' : (t('admin.common.delete') || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Custom Page Create / Edit Modal Component
// ============================================================================

interface CustomPageModalProps {
  page: CmsPage | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSubmitting: boolean;
}

const CustomPageModal: React.FC<CustomPageModalProps> = ({
  page,
  isOpen,
  onClose,
  onSave,
  isSubmitting,
}) => {
  const [title, setTitle] = useState(page?.title || '');
  const [slug, setSlug] = useState(page?.slug || '');
  const [locale, setLocale] = useState(page?.locale || 'en');
  const [metaTitle, setMetaTitle] = useState(page?.metaTitle || '');
  const [metaDescription, setMetaDescription] = useState(page?.metaDescription || '');
  const [sortOrder, setSortOrder] = useState(page?.sortOrder ?? 0);
  const [content, setContent] = useState(page?.content || '');
  const [isPublished, setIsPublished] = useState(page ? page.isPublished : true);
  const [previewMode, setPreviewMode] = useState<'edit' | 'preview'>('edit');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      slug: slug.trim(),
      locale,
      metaTitle: metaTitle.trim() ? metaTitle.trim() : undefined,
      metaDescription: metaDescription.trim() ? metaDescription.trim() : undefined,
      sortOrder: Number(sortOrder),
      content,
      isPublished,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="w-full max-w-3xl my-8 rounded-2xl bg-surface border border-surface-border shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <FileCode className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-sm text-foreground">
              {page ? 'Edit Custom Page' : 'Create New Custom Page'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Page Title *
              </label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Terms of Service"
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                URL Slug *
              </label>
              <input
                type="text"
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. terms or privacy"
                className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Language / Locale
              </label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="en">English (en)</option>
                <option value="vi">Tiếng Việt (vi)</option>
                <option value="ja">日本語 (ja)</option>
                <option value="ko">한국어 (ko)</option>
                <option value="es">Español (es)</option>
                <option value="zh">中文 (zh)</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                SEO Meta Title
              </label>
              <input
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder="Title shown in Google search..."
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                SEO Meta Description
              </label>
              <input
                type="text"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Short description for search snippets..."
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Markdown Content */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-semibold text-muted-fg">
                Page Content (Markdown format) *
              </label>
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface-elevated border border-surface-border text-[11px]">
                <button
                  type="button"
                  onClick={() => setPreviewMode('edit')}
                  className={`px-2 py-0.5 rounded ${previewMode === 'edit' ? 'bg-primary text-white font-medium' : 'text-muted-fg'}`}
                >
                  Write
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode('preview')}
                  className={`px-2 py-0.5 rounded ${previewMode === 'preview' ? 'bg-primary text-white font-medium' : 'text-muted-fg'}`}
                >
                  Preview
                </button>
              </div>
            </div>

            {previewMode === 'edit' ? (
              <textarea
                required
                rows={12}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="# Page Heading&#10;&#10;Write page body in Markdown..."
                className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            ) : (
              <div className="w-full min-h-[240px] max-h-[300px] overflow-y-auto p-4 rounded-lg bg-surface-elevated border border-surface-border text-xs text-foreground prose prose-invert prose-xs max-w-none">
                <pre className="whitespace-pre-wrap font-sans">{content || 'No content written yet.'}</pre>
              </div>
            )}
          </div>

          {/* Visibility Switch */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-surface-border">
            <div>
              <span className="text-xs font-semibold text-foreground">Page Published</span>
              <p className="text-[10px] text-muted-fg">If active, visitors can view this page directly via URL.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Page'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

